import React, { useState } from 'react';
import { CheckIcon, UsersIcon, ArrowDownIcon } from 'lucide-react';
import { formulas, process } from '../data/catering';
import { SectionTitle } from '../components/SectionTitle';
import { QuoteForm } from '../components/catering/QuoteForm';
type Audience = 'tous' | 'pro' | 'prive';
const audiences: {
  id: Audience;
  label: string;
}[] = [{
  id: 'tous',
  label: 'Tout voir'
}, {
  id: 'pro',
  label: 'Professionnels'
}, {
  id: 'prive',
  label: 'Particuliers'
}];
export function Catering() {
  const [audience, setAudience] = useState<Audience>('tous');
  const visible = formulas.filter(f => audience === 'tous' || f.audience === audience);
  return <main className="paper-texture" data-id="element-256" data-mp="2vckn5sjyu6hs">
      <section className="border-b-[3px] border-noir bg-noir py-12 md:py-20" data-id="element-257" data-mp="308i5u5ipboxq">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 md:px-8 lg:grid-cols-[1.1fr_0.9fr]" data-id="element-258" data-mp="1jvb6tew4vukp">
          <div data-id="element-259" data-mp="1ounv842nl9pe">
            <span className="inline-block -rotate-1 border-[3px] border-noir bg-jaune px-3 py-1 font-display text-xs uppercase tracking-[0.16em]" data-id="element-260" data-mp="1ov7afd0x5jzq">
              Traiteur événementiel
            </span>
            <h1 className="mt-4 font-display text-4xl uppercase leading-[0.88] text-creme md:text-6xl" data-id="element-261" data-mp="1mqcmdua4b8jg">
              Votre événement,
              <br data-id="element-262" data-mp="20mznm993skv6" />
              <span className="text-jaune" data-id="element-263" data-mp="1vpzgrw4fje30">version street food</span>
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-creme/80" data-id="element-264" data-mp="1vsc8h0zzp2t9">
              Séminaires, inaugurations, mariages, fêtes de village : on déplace le camion ou
              on livre un buffet, de 15 à 300 convives. Tout est préparé dans notre laboratoire.
            </p>
            <div className="mt-8 flex flex-wrap gap-3" data-id="element-265" data-mp="2jhoe5qw3ysqt">
              <a href="#devis" className="inline-flex items-center gap-2 border-[3px] border-noir bg-jaune px-6 py-3.5 font-display text-base uppercase shadow-hard transition-transform duration-150 ease-out hover:-translate-y-1" data-id="element-266" data-mp="2fdqhdmu99u0x">
                Demander un devis
                <ArrowDownIcon className="h-4 w-4" aria-hidden="true" data-id="element-267" data-mp="1m2jx3fdsnp0i" />
              </a>
              <a href="#formules" className="inline-flex items-center border-[3px] border-creme px-6 py-3.5 font-display text-base uppercase text-creme transition-colors duration-150 ease-out hover:bg-creme hover:text-noir" data-id="element-268" data-mp="1q3vq7uvvmsdg">
                Voir les formules
              </a>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-269" data-mp="24u321rrdpd56">
            <img src="https://cdn.magicpatterns.com/uploads/gjFJV4kQR3c8easbydbJNb/aac76c87-ca11-491f-a64d-47e6acd7f5e8-1_all_1159.jpg" alt="Buffet de mini-sandwichs et planches de fromages sur nappe rouge" className="col-span-2 h-48 w-full border-[3px] border-creme object-cover md:h-60" data-id="element-270" data-mp="29pia232pgeto" />
            <img src="https://cdn.magicpatterns.com/uploads/pf7bDWxt4Hg2QdEZZtFdQ5/1000001746.jpg" alt="Quesadillas coupées en parts et piquées de brochettes" className="h-32 w-full border-[3px] border-creme object-cover md:h-40" data-id="element-271" data-mp="jgrr7oeulc89" />
            <img src="https://cdn.magicpatterns.com/uploads/dLWunSuwSbBZHunctg9Z2N/1000001280.jpg" alt="Verrines alignées, garnies de chips de chorizo" className="h-32 w-full border-[3px] border-creme object-cover md:h-40" data-id="element-272" data-mp="c1654cl713ph" />
          </div>
        </div>
      </section>

      <section id="formules" className="border-b-[3px] border-noir py-16 md:py-24" data-id="element-273" data-mp="utqql8ykc6j9">
        <div className="mx-auto max-w-7xl px-4 md:px-8" data-id="element-274" data-mp="urxlvz7ifwdw">
          <SectionTitle kicker="Nos formules" title="Trois formats, un seul standard" intro="Chaque formule est un point de départ : le menu final est construit avec vous." data-id="element-275" data-mp="ptchrop50sk6" />

          <div role="group" aria-label="Filtrer les formules" className="mt-8 inline-flex border-[3px] border-noir" data-id="element-276" data-mp="3am7o1nlxzsbr">
            {audiences.map(option => <button key={option.id} type="button" onClick={() => setAudience(option.id)} aria-pressed={audience === option.id} className={`border-r-[3px] border-noir px-4 py-2 font-display text-sm uppercase last:border-r-0 transition-colors duration-150 ease-out ${audience === option.id ? 'bg-noir text-jaune' : 'bg-white hover:bg-jaune'}`} data-id="element-277" data-mp="y8ib7gp51lkc">
                {option.label}
              </button>)}
          </div>

          <ul className="mt-8 grid gap-6 lg:grid-cols-3" data-id="element-278" data-mp="17zd06strqz9h">
            {visible.map(formula => <li key={formula.id} className={`flex flex-col border-[3px] border-noir ${formula.featured ? 'bg-jaune shadow-hard' : 'bg-white'}`} data-id="element-279" data-mp="1c0yemilyblls">
                {formula.image && <img src={formula.image} alt={formula.name} className="h-40 w-full border-b-[3px] border-noir object-cover" data-id="element-280" data-mp="3p8g1dolrcrfl" />}
                <div className="flex flex-1 flex-col p-6" data-id="element-281" data-mp="30gc48qlsb94">
                  <div className="flex items-start justify-between gap-3" data-id="element-282" data-mp="3u7sugr382uqm">
                    <h3 className="font-display text-2xl uppercase leading-none" data-id="element-283" data-mp="3u5pw9jrx1vat">{formula.name}</h3>
                    {formula.featured && <span className="shrink-0 border-2 border-noir bg-rouge px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-creme" data-id="element-284" data-mp="31jn8k10fmzp7">
                        Le plus demandé
                      </span>}
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-noir/70" data-id="element-285" data-mp="3bfgjjfyi6l4v">{formula.headline}</p>

                  <p className="mt-4 font-display text-3xl leading-none" data-id="element-286" data-mp="2npmal9hb1gpw">
                    {formula.from}
                  </p>
                  <p className="mt-1.5 flex items-center gap-1.5 text-xs uppercase tracking-[0.1em] text-noir/60" data-id="element-287" data-mp="38hb0f8cliaoh">
                    <UsersIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-288" data-mp="3chl7ypg1d95g" />
                    {formula.guests}
                  </p>

                  <ul className="mt-5 space-y-2 border-t-[3px] border-noir pt-5" data-id="element-289" data-mp="3h9v1efgd0rch">
                    {formula.includes.map(line => <li key={line} className="flex gap-2 text-sm" data-id="element-290" data-mp="2pi6ui8gcox11">
                        <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-canard" aria-hidden="true" data-id="element-291" data-mp="2pk03huefrtc7" />
                        {line}
                      </li>)}
                  </ul>

                  <a href="#devis" className={`mt-auto pt-6 font-display text-sm uppercase underline decoration-[3px] underline-offset-4 transition-colors duration-150 ease-out hover:text-rouge`} data-id="element-292" data-mp="2yky8og0m26wd">
                    Demander cette formule
                  </a>
                </div>
              </li>)}
          </ul>
        </div>
      </section>

      <section className="border-b-[3px] border-noir bg-canard py-16 md:py-24" data-id="element-293" data-mp="1yaos62yh8obf">
        <div className="mx-auto max-w-7xl px-4 md:px-8" data-id="element-294" data-mp="1ycrgmijze9e1">
          <SectionTitle kicker="Comment ça marche" tone="light" title="De votre demande au jour J" data-id="element-295" data-mp="1tidt12bk96jx" />
          <ol className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4" data-id="element-296" data-mp="1oirbwhsm365u">
            {process.map((step, index) => <li key={step.title} className="border-[3px] border-creme bg-canard-dark p-6" data-id="element-297" data-mp="1fje4duo1srnr" data-mpi={index}>
                <span className="inline-flex h-9 w-9 items-center justify-center border-[3px] border-noir bg-jaune font-display text-lg leading-none" data-id="element-298" data-mp="1akkmlq1qaehs">
                  {index + 1}
                </span>
                <h3 className="mt-4 font-display text-xl uppercase leading-none text-creme" data-id="element-299" data-mp="34zdizrafr9as">
                  {step.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-creme/75" data-id="element-300" data-mp="300ace2et4v0p">{step.text}</p>
              </li>)}
          </ol>
        </div>
      </section>

      <section id="devis" className="py-16 md:py-24" data-id="element-301" data-mp="2dtrcl62migts">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 md:px-8 lg:grid-cols-[0.85fr_1.15fr]" data-id="element-302" data-mp="2dvm2yzwnz159">
          <div data-id="element-303" data-mp="2000bmm2yk59o">
            <SectionTitle kicker="Devis gratuit" title="Parlons de votre événement" intro="Donnez-nous les grandes lignes, on construit le menu. Les dates de mai à septembre partent vite : n’hésitez pas à nous écrire tôt." data-id="element-304" data-mp="243ombdmvzgz5" />
            <dl className="mt-8 space-y-4 border-t-[3px] border-noir pt-6 text-sm" data-id="element-305" data-mp="1v0x8x8agab4v">
              <div data-id="element-306" data-mp="1h51w9k6n9w9x">
                <dt className="font-display uppercase" data-id="element-307" data-mp="1h5lg56fuuuql">Zone d’intervention</dt>
                <dd className="text-noir/70" data-id="element-308" data-mp="1l53xjs8gwnoo">80 km autour du laboratoire, au-delà sur devis.</dd>
              </div>
              <div data-id="element-309" data-mp="3bffa5uatf56w">
                <dt className="font-display uppercase" data-id="element-310" data-mp="3fileuxia4ob9">Délai de réservation</dt>
                <dd className="text-noir/70" data-id="element-311" data-mp="3fgiogcd7st60">3 semaines minimum pour les événements de plus de 100 convives.</dd>
              </div>
              <div data-id="element-312" data-mp="zug3zoubihqz">
                <dt className="font-display uppercase" data-id="element-313" data-mp="zuy26r0jj61j">Régimes & allergènes</dt>
                <dd className="text-noir/70" data-id="element-314" data-mp="1dlkxsab08fte">Options végétariennes, sans porc et sans gluten disponibles.</dd>
              </div>
            </dl>
          </div>
          <QuoteForm data-id="element-315" data-mp="18srsnlbmj5gs" />
        </div>
      </section>
    </main>;
}